import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { submitSimpleContact, submitDetailedContact } from "../mock";
import TechnicalQuoteForm from "../components/TechnicalQuoteForm";
import RecaptchaWrapper from "../components/RecaptchaWrapper";
import { Phone, Mail, MapPin, Clock, Send, FileText } from "lucide-react";

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeForm, setActiveForm] = useState("simple"); // "simple", "detailed", or "technical"
  const [recaptchaToken, setRecaptchaToken] = useState("");

  const [simpleForm, setSimpleForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const [detailedForm, setDetailedForm] = useState({
    name: "",
    email: "",
    phone: "",
    equipmentType: "",
    capacity: "",
    sector: "",
    deadline: "",
    message: ""
  });

  const handleSimpleSubmit = async (e) => {
    e.preventDefault();
    
    if (!recaptchaToken) {
      const errorMessage = language === "fr" 
        ? "Veuillez compléter la vérification reCAPTCHA."
        : "Please complete the reCAPTCHA verification.";
      toast.error(errorMessage);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await submitSimpleContact({...simpleForm, recaptcha_token: recaptchaToken}, language);
      if (result.success) {
        toast.success(result.message);
        setSimpleForm({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: ""
        });
        setRecaptchaToken("");
      } else {
        toast.error(result.message || t("error"));
      }
    } catch (error) {
      console.error("Contact form error:", error);
      const errorMessage = language === "fr" 
        ? "Une erreur est survenue. Veuillez réessayer."
        : "An error occurred. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDetailedSubmit = async (e) => {
    e.preventDefault();
    
    if (!recaptchaToken) {
      const errorMessage = language === "fr" 
        ? "Veuillez compléter la vérification reCAPTCHA."
        : "Please complete the reCAPTCHA verification.";
      toast.error(errorMessage);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await submitDetailedContact({...detailedForm, recaptcha_token: recaptchaToken}, language);
      if (result.success) {
        toast.success(result.message);
        setDetailedForm({
          name: "",
          email: "",
          phone: "",
          equipmentType: "",
          capacity: "",
          sector: "",
          deadline: "",
          message: ""
        });
        setRecaptchaToken("");
      } else {
        toast.error(result.message || t("error"));
      }
    } catch (error) {
      console.error("Contact form error:", error);
      const errorMessage = language === "fr" 
        ? "Une erreur est survenue. Veuillez réessayer."
        : "An error occurred. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // reCAPTCHA handlers
  const handleRecaptchaVerify = (token) => {
    setRecaptchaToken(token);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken("");
  };

  const handleRecaptchaError = () => {
    setRecaptchaToken("");
    const errorMessage = language === "fr" 
      ? "Erreur reCAPTCHA. Veuillez réessayer."
      : "reCAPTCHA error. Please try again.";
    toast.error(errorMessage);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-excel-steel to-excel-dark py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-black">
              Contact
            </h1>
            <p className="text-xl max-w-3xl mx-auto leading-relaxed text-black">
              Contactez nos experts pour une consultation personnalisée et un devis adapté à vos besoins.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-excel-dark mb-6">
                  Informations de contact
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-excel-light-red p-3 rounded-full">
                      <Phone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-excel-dark mb-1">Téléphone</h3>
                      <p className="text-gray-600">{t("phone")}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="bg-excel-light-red p-3 rounded-full">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-excel-dark mb-1">Email</h3>
                      <p className="text-gray-600">{t("email")}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="bg-excel-light-red p-3 rounded-full">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-excel-dark mb-1">Adresse</h3>
                      <p className="text-gray-600">{t("address")}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="bg-excel-light-red p-3 rounded-full">
                      <Clock className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-excel-dark mb-1">Heures d'ouverture</h3>
                      <div className="text-gray-600 space-y-1">
                        <p>Lun - Ven: 8h00 - 17h00</p>
                        <p>Sam: 9h00 - 12h00</p>
                        <p>Dim: Fermé</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <Card className="border-l-4 border-l-excel-light-red bg-red-50">
                <CardHeader>
                  <CardTitle className="text-excel-dark flex items-center">
                    <Phone className="h-5 w-5 mr-2" />
                    Service d'urgence 24/7
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-2">
                    Pour les urgences de maintenance et réparations critiques.
                  </p>
                  <p className="font-semibold text-excel-red">
                    1-800-EXCEL-24
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Forms */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* Form Type Selector */}
              <div className="flex space-x-4 border-b">
                <button
                  onClick={() => setActiveForm("simple")}
                  className={`pb-4 px-2 font-medium transition-colors border-b-2 ${
                    activeForm === "simple"
                      ? "text-excel-red border-excel-red"
                      : "text-gray-500 border-transparent hover:text-excel-red"
                  }`}
                >
                  Contact simple
                </button>
                <button
                  onClick={() => setActiveForm("detailed")}
                  className={`pb-4 px-2 font-medium transition-colors border-b-2 ${
                    activeForm === "detailed"
                      ? "text-excel-red border-excel-red"
                      : "text-gray-500 border-transparent hover:text-excel-red"
                  }`}
                >
                  Demande projet
                </button>
                <button
                  onClick={() => setActiveForm("technical")}
                  className={`pb-4 px-2 font-medium transition-colors border-b-2 ${
                    activeForm === "technical"
                      ? "text-excel-red border-excel-red"
                      : "text-gray-500 border-transparent hover:text-excel-red"
                  }`}
                >
                  Devis technique
                </button>
              </div>

              {/* Simple Form */}
              {activeForm === "simple" && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-excel-dark">Contact rapide</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSimpleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="simple-name">{t("name")} *</Label>
                          <Input
                            id="simple-name"
                            type="text"
                            required
                            value={simpleForm.name}
                            onChange={(e) => setSimpleForm({...simpleForm, name: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="simple-email">{t("emailLabel")} *</Label>
                          <Input
                            id="simple-email"
                            type="email"
                            required
                            value={simpleForm.email}
                            onChange={(e) => setSimpleForm({...simpleForm, email: e.target.value})}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="simple-phone">{t("phoneLabel")}</Label>
                          <Input
                            id="simple-phone"
                            type="tel"
                            value={simpleForm.phone}
                            onChange={(e) => setSimpleForm({...simpleForm, phone: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="simple-subject">{t("subject")} *</Label>
                          <Input
                            id="simple-subject"
                            type="text"
                            required
                            value={simpleForm.subject}
                            onChange={(e) => setSimpleForm({...simpleForm, subject: e.target.value})}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="simple-message">{t("message")} *</Label>
                        <Textarea
                          id="simple-message"
                          rows={5}
                          required
                          value={simpleForm.message}
                          onChange={(e) => setSimpleForm({...simpleForm, message: e.target.value})}
                        />
                      </div>

                      <RecaptchaWrapper
                        onVerify={handleRecaptchaVerify}
                        onExpired={handleRecaptchaExpired}
                        onError={handleRecaptchaError}
                        disabled={isSubmitting}
                      />

                      <Button 
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-excel-light-red hover:bg-excel-red text-white"
                      >
                        {isSubmitting ? (
                          "Envoi en cours..."
                        ) : (
                          <>
                            <Send className="mr-2 h-4 w-4" />
                            {t("sendRequest")}
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Detailed Form */}
              {activeForm === "detailed" && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-excel-dark">Demande de projet détaillée</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleDetailedSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="detailed-name">{t("name")} *</Label>
                          <Input
                            id="detailed-name"
                            type="text"
                            required
                            value={detailedForm.name}
                            onChange={(e) => setDetailedForm({...detailedForm, name: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="detailed-email">{t("emailLabel")} *</Label>
                          <Input
                            id="detailed-email"
                            type="email"
                            required
                            value={detailedForm.email}
                            onChange={(e) => setDetailedForm({...detailedForm, email: e.target.value})}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="detailed-phone">{t("phoneLabel")} *</Label>
                          <Input
                            id="detailed-phone"
                            type="tel"
                            required
                            value={detailedForm.phone}
                            onChange={(e) => setDetailedForm({...detailedForm, phone: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="equipment-type">{t("equipmentType")} *</Label>
                          <Select 
                            value={detailedForm.equipmentType}
                            onValueChange={(value) => setDetailedForm({...detailedForm, equipmentType: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionnez un équipement" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="overhead-crane">Pont roulant</SelectItem>
                              <SelectItem value="hoist">Palan électrique</SelectItem>
                              <SelectItem value="winch">Treuil</SelectItem>
                              <SelectItem value="handling-system">Système de manutention</SelectItem>
                              <SelectItem value="maintenance">Service de maintenance</SelectItem>
                              <SelectItem value="other">Autre</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="capacity">{t("capacity")}</Label>
                          <Select 
                            value={detailedForm.capacity}
                            onValueChange={(value) => setDetailedForm({...detailedForm, capacity: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Capacité requise" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1-3t">1-3 tonnes</SelectItem>
                              <SelectItem value="3-10t">3-10 tonnes</SelectItem>
                              <SelectItem value="10-25t">10-25 tonnes</SelectItem>
                              <SelectItem value="25-50t">25-50 tonnes</SelectItem>
                              <SelectItem value="50t+">50+ tonnes</SelectItem>
                              <SelectItem value="custom">Sur mesure</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="sector">{t("sector")}</Label>
                          <Select 
                            value={detailedForm.sector}
                            onValueChange={(value) => setDetailedForm({...detailedForm, sector: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Votre secteur d'activité" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="manufacturing">Manufacturier</SelectItem>
                              <SelectItem value="agrifood">Agroalimentaire</SelectItem>
                              <SelectItem value="energy">Énergie</SelectItem>
                              <SelectItem value="transport">Transport & logistique</SelectItem>
                              <SelectItem value="construction">Construction</SelectItem>
                              <SelectItem value="other">Autre</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="deadline">{t("deadline")}</Label>
                        <Select 
                          value={detailedForm.deadline}
                          onValueChange={(value) => setDetailedForm({...detailedForm, deadline: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Échéance souhaitée" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="urgent">Urgent (moins de 1 mois)</SelectItem>
                            <SelectItem value="1-3months">1-3 mois</SelectItem>
                            <SelectItem value="3-6months">3-6 mois</SelectItem>
                            <SelectItem value="6months+">6 mois et plus</SelectItem>
                            <SelectItem value="flexible">Flexible</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="detailed-message">Description du projet *</Label>
                        <Textarea
                          id="detailed-message"
                          rows={5}
                          required
                          placeholder="Décrivez votre projet, vos contraintes spécifiques, l'environnement d'installation..."
                          value={detailedForm.message}
                          onChange={(e) => setDetailedForm({...detailedForm, message: e.target.value})}
                        />
                      </div>

                      <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-lg">
                        <FileText className="h-5 w-5 text-excel-light-red" />
                        <div className="text-sm text-gray-700">
                          <p className="font-medium">Documents à joindre (optionnel)</p>
                          <p>Plans, spécifications techniques, photos de l'installation existante</p>
                        </div>
                      </div>

                      <RecaptchaWrapper
                        onVerify={handleRecaptchaVerify}
                        onExpired={handleRecaptchaExpired}
                        onError={handleRecaptchaError}
                        disabled={isSubmitting}
                      />

                      <Button 
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-excel-light-red hover:bg-excel-red text-white"
                      >
                        {isSubmitting ? (
                          "Envoi en cours..."
                        ) : (
                          <>
                            <Send className="mr-2 h-4 w-4" />
                            Envoyer ma demande détaillée
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Technical Quote Form */}
              {activeForm === "technical" && (
                <div className="space-y-6">
                  <TechnicalQuoteForm />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Nos Bureaux Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-excel-dark mb-4 font-montserrat">
              Nos bureaux
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Excel Levage vous accompagne à travers ses 5 bureaux stratégiquement situés pour mieux vous servir
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Map Section */}
            <div className="relative">
              <div className="bg-white rounded-lg shadow-lg p-6 h-96">
                <div className="h-full bg-gray-100 rounded-lg flex items-center justify-center relative overflow-hidden">
                  {/* Interactive Map Placeholder */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-green-100">
                    {/* Map markers for each office */}
                    <div className="absolute top-1/4 left-1/3 w-4 h-4 bg-excel-red rounded-full border-2 border-white shadow-lg animate-pulse cursor-pointer group">
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-excel-dark text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        Montréal
                      </div>
                    </div>
                    <div className="absolute top-2/5 left-1/2 w-4 h-4 bg-excel-red rounded-full border-2 border-white shadow-lg animate-pulse cursor-pointer group">
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-excel-dark text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        Drummondville
                      </div>
                    </div>
                    <div className="absolute top-3/5 left-2/3 w-4 h-4 bg-excel-red rounded-full border-2 border-white shadow-lg animate-pulse cursor-pointer group">
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-excel-dark text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        Sherbrooke
                      </div>
                    </div>
                    <div className="absolute top-1/3 right-1/4 w-4 h-4 bg-excel-red rounded-full border-2 border-white shadow-lg animate-pulse cursor-pointer group">
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-excel-dark text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        Lyon, France
                      </div>
                    </div>
                    <div className="absolute bottom-1/4 right-1/3 w-4 h-4 bg-excel-red rounded-full border-2 border-white shadow-lg animate-pulse cursor-pointer group">
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-excel-dark text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        Alger, Algérie
                      </div>
                    </div>
                  </div>
                  <div className="text-center text-gray-600">
                    <MapPin className="h-8 w-8 mx-auto mb-2 text-excel-red" />
                    <p className="text-lg font-medium">Carte interactive de nos bureaux</p>
                    <p className="text-sm">Survolez les marqueurs pour plus d'informations</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Offices List */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                {/* Montreal */}
                <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-excel-red">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-excel-red rounded-full flex items-center justify-center">
                          <MapPin className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-lg font-bold text-excel-dark mb-2">Montréal (Siège social)</h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>📍 1234 Boulevard Industriel, Montréal, QC H4B 2V7</p>
                          <p>📞 (514) 555-0123</p>
                          <p>✉️ montreal@excel-levage.com</p>
                          <p>🕒 Lun-Ven: 8h-17h</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Drummondville */}
                <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-excel-light-red">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-excel-light-red rounded-full flex items-center justify-center">
                          <MapPin className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-lg font-bold text-excel-dark mb-2">Drummondville</h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>📍 567 Rue des Forges, Drummondville, QC J2B 1E5</p>
                          <p>📞 (819) 555-0456</p>
                          <p>✉️ drummondville@excel-levage.com</p>
                          <p>🕒 Lun-Ven: 8h-17h</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Sherbrooke */}
                <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-excel-steel">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-excel-steel rounded-full flex items-center justify-center">
                          <MapPin className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-lg font-bold text-excel-dark mb-2">Sherbrooke</h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>📍 890 Rue King Ouest, Sherbrooke, QC J1H 1R3</p>
                          <p>📞 (819) 555-0789</p>
                          <p>✉️ sherbrooke@excel-levage.com</p>
                          <p>🕒 Lun-Ven: 8h-17h</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Lyon */}
                <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-blue-500">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                          <MapPin className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-lg font-bold text-excel-dark mb-2">Lyon, France</h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>📍 15 Avenue Tony Garnier, 69007 Lyon, France</p>
                          <p>📞 +33 4 72 76 01 23</p>
                          <p>✉️ lyon@excel-levage.fr</p>
                          <p>🕒 Lun-Ven: 9h-18h (CET)</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Alger */}
                <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-green-500">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                          <MapPin className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-lg font-bold text-excel-dark mb-2">Alger, Algérie</h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>📍 Zone Industrielle Rouiba, Alger, Algérie</p>
                          <p>📞 +213 21 85 64 32</p>
                          <p>✉️ alger@excel-levage.dz</p>
                          <p>🕒 Sam-Jeu: 8h-16h (CET)</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;